import { z } from 'zod';
import { FIELD_KEY_PATTERN, MAX_FIELD_KEY_LENGTH } from './field-key.js';
import { multiLanguageContentSchema } from './i18n.js';
/**
 * Visitor types — the category a visitor picks on the kiosk (Interview, Meeting,
 * Guest, Contractor, Other, plus whatever a tenant adds), and the dimension a
 * check-in flow is bound to.
 *
 * Scope mirrors `VisitorCheckInFlow`: a tenant-level type (`locationId: null`)
 * is inherited by every location; a location-only type belongs to one. Whether
 * a location's kiosk OFFERS a type is a per-location fact carried on the DTO as
 * `isVisibleAtLocation`, resolved against the `locationId` the list was asked
 * for — an inherited tenant row cannot hold one location's preference.
 */
/** Seeded for every tenant at creation, in kiosk order. `isSystem`, undeletable. */
export const SYSTEM_VISITOR_TYPE_KEYS = ['interview', 'meeting', 'guest', 'contractor', 'other'];
export const systemVisitorTypeKeySchema = z.enum(SYSTEM_VISITOR_TYPE_KEYS);
export function isSystemVisitorTypeKey(key) {
    return SYSTEM_VISITOR_TYPE_KEYS.includes(key);
}
/** Same grammar as a field key: `deriveFieldKey` produces it from the English label. */
export const visitorTypeKeySchema = z
    .string()
    .trim()
    .max(MAX_FIELD_KEY_LENGTH)
    .regex(FIELD_KEY_PATTERN, { message: 'INVALID_VISITOR_TYPE_KEY' });
/** Admin-input label bound, matching `labelContentSchema` in ./check-in-flow.ts. */
export const MAX_VISITOR_TYPE_LABEL_CHARS = 120;
const labelContentSchema = multiLanguageContentSchema.superRefine((value, ctx) => {
    for (const [locale, text] of Object.entries(value)) {
        if (typeof text === 'string' && text.length > MAX_VISITOR_TYPE_LABEL_CHARS) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [locale], message: 'CONTENT_TOO_LONG' });
        }
    }
});
/** Derived from `locationId`, same as `FLOW_SCOPES`. */
export const VISITOR_TYPE_SCOPES = ['TENANT', 'LOCATION'];
export const visitorTypeScopeSchema = z.enum(VISITOR_TYPE_SCOPES);
export const visitorTypeDTOSchema = z.object({
    id: z.string().min(1),
    key: z.string().min(1),
    label: multiLanguageContentSchema,
    scope: visitorTypeScopeSchema,
    locationId: z.string().min(1).nullable(),
    sortOrder: z.number().int(),
    isSystem: z.boolean(),
    isActive: z.boolean(),
    /**
     * Whether a visit of this type may have a badge printed. `true` is the default.
     * A badge only prints when this AND the check-in location's own
     * `badgePrintingEnabled` (`KioskSettingValues`) are both `true` — see
     * `packages/db/prisma/schema.prisma`'s `VisitorType.badgePrintingEnabled` comment.
     */
    badgePrintingEnabled: z.boolean(),
    /**
     * Whether the location the list was asked for offers this type on its kiosk.
     * `null` when the list was not scoped to a location — there is no location
     * to answer for. Never `false` by default: an absent override means visible.
     */
    isVisibleAtLocation: z.boolean().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * `GET /api/web/visitor-types` — `visitor.flow.read`.
 *
 * With `locationId`: the tenant-level rows plus that location's own, each with
 * `isVisibleAtLocation` resolved. Without: every row in the tenant (the
 * location-only ones included), `isVisibleAtLocation: null`. Inactive rows are
 * excluded unless `includeInactive`.
 */
export const visitorTypeListQuerySchema = z.object({
    locationId: z.string().min(1).optional(),
    includeInactive: z.coerce.boolean().default(false),
});
/**
 * `POST /api/web/visitor-types` — `visitor.flow.manage`.
 *
 * `key` optional: omitted, the server derives it from `label.en` with
 * `deriveFieldKey` and suffixes on collision. `locationId` null = tenant-level,
 * which only a tenant-wide administrator may create.
 */
export const createVisitorTypeSchema = z.object({
    key: visitorTypeKeySchema.optional(),
    label: labelContentSchema,
    locationId: z.string().min(1).nullable().default(null),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    badgePrintingEnabled: z.boolean().optional(),
});
/**
 * `PATCH /api/web/visitor-types/:id` — `visitor.flow.manage`.
 *
 * `.strict()` so a `key` or `locationId` in the body is refused rather than
 * dropped (see `updateCheckInFieldSchema` for why silent drops are the bug).
 * `isActive: false` on a system type is refused by the service.
 */
export const updateVisitorTypeSchema = z
    .object({
    label: labelContentSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    isActive: z.boolean().optional(),
    badgePrintingEnabled: z.boolean().optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'EMPTY_UPDATE' });
    }
});
/** `PUT /api/web/locations/:id/visitor-types/:typeId/visibility` — `visitor.flow.manage`. */
export const setVisitorTypeVisibilitySchema = z.object({
    isVisible: z.boolean(),
});
/**
 * Every code the visitor-type endpoints can answer with, so a console can map
 * each to a translated message. Same contract as `FLOW_VALIDATION_CODES`.
 */
export const VISITOR_TYPE_VALIDATION_CODES = [
    'EMPTY_UPDATE',
    'INVALID_VISITOR_TYPE_KEY',
    'EN_CONTENT_REQUIRED',
    'CONTENT_TOO_LONG',
    'VISITOR_TYPE_NOT_FOUND',
    'VISITOR_TYPE_KEY_TAKEN',
    'SYSTEM_VISITOR_TYPE_KEY_IS_FIXED',
    'SYSTEM_VISITOR_TYPE_CANNOT_BE_DELETED',
    'SYSTEM_VISITOR_TYPE_CANNOT_BE_DEACTIVATED',
    'VISITOR_TYPE_IN_USE',
    'VISITOR_TYPE_FOREIGN_LOCATION',
    'TENANT_SCOPE_REQUIRES_TENANT_ADMIN',
];
//# sourceMappingURL=visitor-type.js.map